export { NO_UE_CAP } from './azav/lib/noUeCap'
import { erstgespraechForm } from './azav/templates'
import { advanceNewErstgespraech, checkErstgespraech, erstgespraechManualFields } from './azav/flows/erstgespraech'
import type { WriteIdentity } from '@/core/api/http'
import type { ComponentType, ReactNode } from 'react'
import type { Modal, CoachingModal } from '@/core/state/appStore'
import type { FormDefinition, TabOverrides } from '@/core/templates/types'
import type { CardMark } from '@/core/views/viewConfig'
import type {
  CoachingCreate,
  CoachingGueltigkeit,
  CoachingVertragRef,
  CoachingVoucherRef,
} from '@/core/data/coachings'
import type {
  MassnahmeOption,
  MassnahmeCreate,
  MassnahmePatch,
  MassnahmeRow,
  MassnahmeTbFields,
  MassnahmeUeBudget,
} from '@/core/data/massnahmen'
import type { DokumentCreate, DokumentPatch } from '@/core/data/dokumente'
import type { CoachingAzavPatch } from './azav/api/resources'
import { getProfil } from './vergabe/api/teilnehmerakte-staff'
import { ProfilSections } from './vergabe/components/profil/profil-sections'
import { getSpalteStand } from './vergabe/api/spalteStand'
import { AZAV_SUBTYPE } from './azav/data/subtype'
import { createMassnahme, listMassnahmenFull, updateMassnahme } from './azav/data/massnahmen'
import { tbTabOverrides } from './azav/flows/tb'
import { AUFGABE } from '@/core/data/aufgabe'
import { anleitungUrl } from '../../anleitung'
import {
  COLUMNS,
  stageForStatusName,
  statusNameCandidatesFor,
  type AzavStage,
  type Column,
} from './azav/columns'
import { ACTION_TO_MODAL, ENTRY, isDeleteLocked } from './azav/boardConfig'
import {
  ORG_SETTINGS_TABS,
  MASSNAHME_FORM,
  HAS_MODULES,
  HAS_ECN_SETTINGS,
} from './azav/massnahme'
import {
  type VergabeStage,
  COLUMNS as VERGABE_COLUMNS,
  stageForStatusName as vergabeStageForStatusName,
  statusNameCandidatesFor as vergabeStatusNameCandidatesFor,
} from './vergabe/columns'
import { ACTION_TO_MODAL as VERGABE_ACTION_TO_MODAL, ENTRY as VERGABE_ENTRY } from './vergabe/boardConfig'
import { verbisLebenslaufFehlt } from './vergabe/lib/verbisLebenslaufFehlt'
import { TEILNEHMERAKTE_STAGE } from './vergabe/lib/teilnehmerakte'
import { getLoeschStatus } from './vergabe/api/teilnehmerakte-staff'
import { isNichtZurueckgezogen, rueckzugAufheben } from './vergabe/api/rueckzug'
import { zuruecksetzen } from './vergabe/api/zuruecksetzen'
import { Teilnehmersicht } from './vergabe/components/anwesenheit/Teilnehmersicht'
import { UnterrichtsfreiTage } from './vergabe/components/massnahme/UnterrichtsfreiTage'
import { teilnehmersichtStarten } from './vergabe/lib/teilnehmersicht'
import { laufendeUploads } from './vergabe/components/verbis-lebenslauf/laufendeUploads'
import { AUFGABE as VERGABE_AUFGABE } from './vergabe/aufgabe'
import { TESTDATEN_SCHRITTE as VERGABE_TESTDATEN_SCHRITTE, testdatenAnlegen } from './vergabe/flows/testdaten'
import { GEVA_KLETT_DOKUMENTE } from './vergabe/api/gevaKlett'
import { HOCHLADBARE_DOKUMENTE } from './vergabe/dokumente'
import { getAktenplan, type Aktenplan } from './vergabe/api/aktenplan'
import { GevaKlettNachreichen } from './vergabe/components/geva-klett/GevaKlettNachreichen'
import { KOMPETENZBILANZIERUNG_DOKUMENT, getKompetenzbilanzierung } from './vergabe/api/kompetenzbilanzierung'
import { KompetenzbilanzierungNachreichen } from './vergabe/components/kompetenzfeststellung/KompetenzbilanzierungNachreichen'
import {
  ORG_SETTINGS_TABS as VERGABE_ORG_SETTINGS_TABS,
  MASSNAHME_FORM as VERGABE_MASSNAHME_FORM,
  HAS_MODULES as VERGABE_HAS_MODULES,
  HAS_ECN_SETTINGS as VERGABE_HAS_ECN_SETTINGS,
} from './vergabe/massnahme'
import {
  createMassnahme as createVergabeMassnahme,
  listMassnahmenFull as listVergabeMassnahmenFull,
  updateMassnahme as updateVergabeMassnahme,
} from './vergabe/api/massnahme'

export type Stage = AzavStage | VergabeStage

export type VariantDescription = {
  columns: Column<Stage>[]
  aufgabe: Record<string, { dbName: string; landing?: { modal: CoachingModal; tab: string } | null }>
  actionToModal: Record<string, CoachingModal>
  stageForStatusName: (name: string | null | undefined) => Stage | null
  statusNameCandidatesFor: (stage: Stage) => string[]
  entry: { label: string; action: string }
  cardIncomplete?: readonly CardIncomplete[]
  waitingLight: boolean
  kohorten: boolean
  zuweisungszeitraum: boolean
  cardActivity?: {
    stage: Stage
    subscribe: (listener: () => void) => () => void
    cardMarks: () => ReadonlyMap<string, CardMark>
  }
  isDeleteLocked?: (coaching: { stage: Stage; documents: readonly string[] }) => boolean
  getLoeschStatus?: (coachingId: string) => Promise<{ unterschrieben: boolean; darfLoeschen: boolean }>
  orgSettingsTabs: readonly string[]
  massnahmeForm: FormDefinition
  massnahmen: MassnahmeAccess
  hasModules: boolean
  firstConversation?: {
    form: FormDefinition
    check: typeof checkErstgespraech
    manualFields: typeof erstgespraechManualFields
    advance: typeof advanceNewErstgespraech
  }
  hasEcnSettings: boolean
  anleitung: string | null
  assertPersonAccess?: (coachingId: string) => Promise<void>
  personOverviewSections?: ComponentType<{ coachingId: string }>
  completedTabs?: (coachingId: string, modal: Modal) => Promise<string[]>
  tabOverrides?: { modal: Modal; load: (coachingId: string) => Promise<TabOverrides> }
  subtype: SubtypeAccess | null
  gelisteteDokumente?: readonly GelistetesDokument[]
  hochladbareDokumente?: readonly string[]
  aktenplan?: () => Promise<Aktenplan>
  testdaten?: { schritte: typeof VERGABE_TESTDATEN_SCHRITTE; anlegen: typeof testdatenAnlegen }
  rueckzug?: RueckzugAufhebung
  zuruecksetzen?: (coachingIds: readonly string[], spalte: string) => Promise<unknown>
  anwesenheit?: { Teilnehmersicht: ComponentType; starten: () => void; UnterrichtsfreiTage: ComponentType<{ massnahmeId: string; isOwner: boolean }> }
}

export type { Aktenplan }

type RueckzugAufhebung = {
  aufheben: (coachingId: string) => Promise<unknown>
  istNichtZurueckgezogen: (error: unknown) => boolean
}

type GelistetesDokument = {
  name: string
  erwartet?: (coachingId: string) => Promise<boolean>
  Nachreichen: ComponentType<{ coachingId: string; name: string; onGespeichert: () => void; onBusyChange?: (busy: boolean) => void; renderHeader: (actions?: ReactNode) => ReactNode }>
  bearbeitbar?: boolean
}

export function immerGelisteteNamen(eintraege: readonly GelistetesDokument[] | undefined): readonly string[] {
  return (eintraege ?? []).filter((e) => !e.erwartet).map((e) => e.name)
}

export async function gelisteteNamenFuer(
  eintraege: readonly GelistetesDokument[] | undefined,
  coachingId: string,
): Promise<readonly string[]> {
  const gelistet = await Promise.all(
    (eintraege ?? []).map((e) =>
      e.erwartet
        ? e.erwartet(coachingId).catch((error: unknown) => {
            console.error(`${e.name} nachschlagen fehlgeschlagen:`, error)
            return false
          })
        : Promise.resolve(true),
    ),
  )
  return (eintraege ?? []).filter((_, i) => gelistet[i]).map((e) => e.name)
}

export const gelistetesDokument = (
  eintraege: readonly GelistetesDokument[] | undefined,
  name: string,
): GelistetesDokument | undefined => eintraege?.find((e) => e.name === name)


export type CardIncomplete = {
  stage: Stage
  isIncomplete?: (coachingId: string) => Promise<boolean>
}

export function cardIncompleteCovers(rule: CardIncomplete, card: { stage: Stage }): boolean {
  return rule.stage === card.stage
}

type MassnahmeAccess = {
  listMassnahmenFull: (organisationId: string) => Promise<MassnahmeRow[]>
  createMassnahme: (organisationId: string, patch: MassnahmeCreate) => Promise<MassnahmeRow>
  updateMassnahme: (id: string, patch: MassnahmePatch) => Promise<void>
}

type SubtypeAccess = {
  getPersonRecords: (coachingId: string) => Promise<import('@/core/data/person').PersonCoachingRecords>
  getGueltigkeit: (coachingId: string) => Promise<CoachingGueltigkeit | null>
  createCoaching: (row: CoachingCreate) => Promise<void>
  patchCoaching: (coachingId: string, patch: CoachingAzavPatch) => Promise<void>
  getCoachingVertragRef: (coachingId: string) => Promise<CoachingVertragRef | null>
  getCoachingVoucherRef: (coachingId: string) => Promise<CoachingVoucherRef | null>
  getMassnahmeUeBudget: (massnahmeId: string) => Promise<MassnahmeUeBudget | null>
  listMassnahmen: (organisationId: string) => Promise<MassnahmeOption[]>
  getMassnahmeTbFields: (organisationId: string, nummer: string) => Promise<MassnahmeTbFields | null>
  createDokument: (row: DokumentCreate, identity?: WriteIdentity) => Promise<string>
  patchDokument: (dokumentId: string, patch: DokumentPatch, identity?: WriteIdentity) => Promise<void>
}

const AZAV: VariantDescription = {
  columns: COLUMNS,
  aufgabe: AUFGABE,
  actionToModal: ACTION_TO_MODAL,
  stageForStatusName,
  statusNameCandidatesFor,
  entry: ENTRY,
  isDeleteLocked,
  waitingLight: true,
  kohorten: false,
  zuweisungszeitraum: false,
  orgSettingsTabs: ORG_SETTINGS_TABS,
  massnahmeForm: MASSNAHME_FORM,
  firstConversation: { form: erstgespraechForm, check: checkErstgespraech, manualFields: erstgespraechManualFields, advance: advanceNewErstgespraech },
  massnahmen: { listMassnahmenFull, createMassnahme, updateMassnahme },
  hasModules: HAS_MODULES,
  hasEcnSettings: HAS_ECN_SETTINGS,
  anleitung: anleitungUrl('azav'),
  subtype: AZAV_SUBTYPE,
  tabOverrides: { modal: 'avgs', load: tbTabOverrides },
}

const VERGABE: VariantDescription = {
  columns: VERGABE_COLUMNS,
  aufgabe: VERGABE_AUFGABE,
  actionToModal: VERGABE_ACTION_TO_MODAL,
  stageForStatusName: vergabeStageForStatusName,
  statusNameCandidatesFor: vergabeStatusNameCandidatesFor,
  entry: VERGABE_ENTRY,
  cardIncomplete: [
    { stage: 'verbis', isIncomplete: verbisLebenslaufFehlt },
    { stage: TEILNEHMERAKTE_STAGE },
    { stage: 'kompetenzfeststellung' },
    { stage: 'kompetenzbilanzierung' },
  ],
  cardActivity: { stage: 'verbis', ...laufendeUploads },
  getLoeschStatus,
  waitingLight: false,
  kohorten: true,
  zuweisungszeitraum: true,
  orgSettingsTabs: VERGABE_ORG_SETTINGS_TABS,
  massnahmeForm: VERGABE_MASSNAHME_FORM,
  massnahmen: {
    listMassnahmenFull: listVergabeMassnahmenFull,
    createMassnahme: createVergabeMassnahme,
    updateMassnahme: updateVergabeMassnahme,
  },
  hasModules: VERGABE_HAS_MODULES,
  hasEcnSettings: VERGABE_HAS_ECN_SETTINGS,
  anleitung: anleitungUrl('vergabe'),
  subtype: null,
  assertPersonAccess: async (coachingId) => {
    await getProfil(coachingId)
  },
  personOverviewSections: ProfilSections,
  completedTabs: async (coachingId, modal) => {
    const { status, erledigt } = await getSpalteStand(coachingId)
    const column = VERGABE_COLUMNS.findIndex((entry) => entry.dbName === status)
    const tasks: import('./vergabe/aufgabe').AufgabeEntry[] = Object.values(VERGABE_AUFGABE)
    const applicable = tasks.filter((task) => task.landing?.modal === modal)
    return [...new Set(applicable.map((task) => task.landing!.tab))]
      .filter((tab) => applicable.filter((task) => task.landing!.tab === tab).every((task) => {
        const owningColumn = VERGABE_COLUMNS.findIndex((entry) => entry.id === task.stage)
        return owningColumn < column || (owningColumn === column && erledigt.includes(task.dbName))
      }))
  },
  gelisteteDokumente: [
    ...GEVA_KLETT_DOKUMENTE.map((name) => ({ name, Nachreichen: GevaKlettNachreichen })),
    {
      name: KOMPETENZBILANZIERUNG_DOKUMENT,
      erwartet: async (coachingId: string) => (await getKompetenzbilanzierung(coachingId)) !== null,
      Nachreichen: KompetenzbilanzierungNachreichen,
      bearbeitbar: true,
    },
  ],
  hochladbareDokumente: HOCHLADBARE_DOKUMENTE,
  aktenplan: getAktenplan,
  testdaten: { schritte: VERGABE_TESTDATEN_SCHRITTE, anlegen: testdatenAnlegen },
  rueckzug: { aufheben: rueckzugAufheben, istNichtZurueckgezogen: isNichtZurueckgezogen },
  zuruecksetzen,
  anwesenheit: { Teilnehmersicht, starten: teilnehmersichtStarten, UnterrichtsfreiTage },
}

export function descriptionFor(variantKey: string): VariantDescription {
  if (variantKey === 'azav') return AZAV
  if (variantKey === 'vergabe') return VERGABE
  throw new Error(`descriptionFor: no board description for variant "${variantKey}" yet`)
}

export function subtypeFor(variantKey: string): SubtypeAccess {
  const access = descriptionFor(variantKey).subtype
  if (!access) {
    throw new Error(`subtypeFor: variant "${variantKey}" has no subtype access for its coachings and documents, they are served on the core paths`)
  }
  return access
}

export { TeilnehmerakteStaffHost } from './vergabe/components/teilnehmerakte/staff/teilnehmerakte-staff-host'
export { useTeilnehmersicht } from './vergabe/lib/teilnehmersicht'


export { VERTRAG_REMINDER_ACTION } from './azav/columns'
export { openVertragCoachings, sendVertragReminders, vertragReminderToast } from './azav/flows/vertragReminder'
export type { UnterschriftDokument } from './vergabe/api/unterschrift'
